import { DateFormats, formatDate } from '@strata/helper-functions';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormHeaderAndContent } from '@strata/react-form-header-and-content';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { TextAreaFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { Navigate } from 'react-router';
import * as Yup from 'yup';

import { changeReferralPeriodForTreatment } from 'api/treatmentAPI';
import { handleError } from 'services/Error';
import { usePermissionCheck, WRITE_REFERRAL_PERIOD } from 'services/Permissions';
import { AppRoutes } from 'services/routes';
import { useGetPayersQuery } from 'store/api/referenceApi';

const ReasonOptions = ['Contractual', 'Data correction', 'Auth update', 'Other'];

export const BulkUpdateReferralExpiration = () => {
    const canUpdateReferralPeriod = usePermissionCheck(WRITE_REFERRAL_PERIOD);
    const { data, isFetching } = useGetPayersQuery(undefined, { skip: !canUpdateReferralPeriod });
    const { payerList } = data ?? {};

    if (!canUpdateReferralPeriod) {
        return <Navigate replace to={AppRoutes.home} />;
    }

    return (
        <FormHeaderAndContent
            mainContent={
                <Formik
                    initialValues={{ setToGrandfathered: false }}
                    validationSchema={Yup.object({
                        payerId: Yup.string().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        treatmentIds: Yup.string()
                            .required(validationStrings.requiredField.errorMessage)
                            .matches(/^.+$/gm, 'IDs formatted incorrectly.'),
                        referralExpiration: DateInputFormik.validator().required(
                            validationStrings.requiredField.errorMessage,
                        ),
                        ...ReasonInput.validationSchema,
                    })}
                    onSubmit={async (values, { resetForm }) => {
                        try {
                            const errors = (
                                await Promise.all(
                                    values.treatmentIds?.match(/^.+$/gm).map((treatmentId) =>
                                        (async () => {
                                            try {
                                                await changeReferralPeriodForTreatment({
                                                    payerId: values.payerId,
                                                    treatmentId: treatmentId.trim(),
                                                    referralExpiration: formatDate(
                                                        values?.referralExpiration,
                                                        DateFormats.HYPHEN_DATE,
                                                    ),
                                                    setToGrandfathered: !!values.setToGrandfathered,
                                                    ...ReasonInput.submitValues(values),
                                                });
                                                return undefined;
                                            } catch {
                                                return treatmentId;
                                            }
                                        })(),
                                    ),
                                )
                            ).filter(Boolean);

                            if (errors.length > 0) {
                                ErrorToast({
                                    message: (
                                        <div>
                                            <div>
                                                The following treatments could not be updated:
                                            </div>
                                            {errors.map((id) => (
                                                <div key={id}>{id}</div>
                                            ))}
                                        </div>
                                    ),
                                    timeout: 0,
                                });
                            } else {
                                SuccessToast({
                                    message: 'Referral expirations updated successfully.',
                                });
                                resetForm();
                            }
                        } catch (err) {
                            handleError(
                                'There was an error attempting this action, please try again.',
                                err,
                            );
                        }
                    }}
                >
                    {(formik) => (
                        <FormContainer
                            formTitle="Bulk Update Referral Expiration"
                            primaryButtonLabel="Update Expirations"
                            onFormSubmit={formik.handleSubmit}
                            onSecondaryButtonClick={formik.handleReset}
                            isSubmitting={formik.isSubmitting || !(formik.isValid && formik.dirty)}
                            formLeftContent={
                                <>
                                    <FullscreenLoading
                                        isShowing={formik.isSubmitting}
                                        loadingText="Updating referral expirations..."
                                    />
                                    <div className="margin__bottom--medium">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.INFO}
                                            wrapText
                                            label="This action updates the active referral expiration for each treatment. Checking Grandfathered marks referrals as grandfathered and does not clear grandfathering."
                                        />
                                    </div>
                                    <SuggestSingleSelectFormik
                                        id="payerId"
                                        label="Health Plan"
                                        required
                                        isLoading={isFetching}
                                        values={payerList}
                                        selectedItem={payerList?.find(
                                            ({ value }) => value === formik.values.payerId,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue('payerId', newValue?.value)
                                        }
                                    />
                                    <TextAreaFormik
                                        label="Treatment IDs"
                                        id="treatmentIds"
                                        helperText="One ID per line."
                                        required
                                    />
                                    <DateInputFormik
                                        label="Referral Expiration"
                                        id="referralExpiration"
                                        required
                                    />
                                    <Checkbox
                                        id="setToGrandfathered"
                                        label="Set to Grandfathered"
                                        checked={!!formik.values.setToGrandfathered}
                                        onChange={(checked) =>
                                            formik.setFieldValue('setToGrandfathered', checked)
                                        }
                                    />
                                    <ReasonInput reasons={ReasonOptions} />
                                </>
                            }
                        />
                    )}
                </Formik>
            }
        />
    );
};
